import { useCallback, useEffect, useRef, useState } from 'react'

import {
  useReportConfigOptions,
  useWizardActions,
  useWizardSelectedSections,
} from '@/modules/analysis'
import type { ConfigSection } from '@/modules/projects'

export default function useDragReorder() {
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const draggedKeyRef = useRef<string | null>(null)
  const reportListRef = useRef<HTMLDivElement>(null)
  const config = useReportConfigOptions()
  const selectedSections = useWizardSelectedSections()
  const pinnedTopSections = config?.pinnedTopSections ?? []
  const sections = config?.sections ?? []

  const { addSection, reorderSections } = useWizardActions()

  const sectionMap = new Map(sections.map((section) => [section.key, section]))

  const reportSections = selectedSections
    .map((key) => sectionMap.get(key))
    .filter(Boolean) as ConfigSection[]

  const reorderableSections = reportSections.filter(({ pinned }) => !pinned)

  const computeReorderableDropIndex = (clientY: number): number => {
    const container = reportListRef.current
    if (!container) return reorderableSections.length

    const items = container.querySelectorAll('[data-reorder-item]')
    for (let index = 0; index < items.length; index++) {
      const rect = items[index].getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      if (clientY < midpoint) return index
    }
    return items.length
  }

  const reorderableIndexToSelectedIndex = (
    reorderableIndex: number,
  ): number => {
    if (reorderableIndex >= reorderableSections.length) {
      const lastReorderableKey =
        reorderableSections[reorderableSections.length - 1]?.key

      if (!lastReorderableKey) {
        return pinnedTopSections.length
      }
      return selectedSections.indexOf(lastReorderableKey) + 1
    }
    const targetKey = reorderableSections[reorderableIndex].key
    return selectedSections.indexOf(targetKey)
  }

  const allowDrop = useCallback((event: DragEvent) => {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  }, [])

  const startDrag = (key: string) => {
    draggedKeyRef.current = key
    document.addEventListener('dragover', allowDrop, true)
    document.addEventListener('dragenter', allowDrop, true)
  }

  const endDrag = () => {
    document.removeEventListener('dragover', allowDrop, true)
    document.removeEventListener('dragenter', allowDrop, true)
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropIndex(computeReorderableDropIndex(event.clientY))
  }

  const handleDragLeave = (event: React.DragEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDropIndex(null)
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const sectionKey = draggedKeyRef.current
    const reorderableIdx = dropIndex ?? reorderableSections.length
    const insertAt = reorderableIndexToSelectedIndex(reorderableIdx)
    setDropIndex(null)
    draggedKeyRef.current = null
    endDrag()

    if (!sectionKey) return

    const isAlreadyInReport = selectedSections.includes(sectionKey)

    if (isAlreadyInReport) {
      const currentIndex = selectedSections.indexOf(sectionKey)
      const without = selectedSections.filter((key) => key !== sectionKey)
      const adjustedIndex = insertAt > currentIndex ? insertAt - 1 : insertAt
      without.splice(adjustedIndex, 0, sectionKey)
      reorderSections(without)
    } else {
      addSection(sectionKey, insertAt)
    }
  }

  useEffect(() => {
    return () => {
      document.removeEventListener('dragover', allowDrop, true)
      document.removeEventListener('dragenter', allowDrop, true)
    }
  }, [allowDrop])

  return {
    dropIndex,
    reportListRef,
    reorderableSections,
    startDrag,
    endDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  }
}
